import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { DeployVersionGuard } from "./components/DeployVersionGuard";
import { ErrorBoundaryWrapper } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import SmartRedirect from "./components/SmartRedirect";

import { AuthProvider } from "./context/AuthContext";

import Dashboard from "./pages/Dashboard/index";
import Login from "./pages/Login/index";
import Loja from "./pages/Loja/index";
import Usuario from "./pages/Usuario/index";
import Entregador from "./pages/Entregador/index";
import Estatisticas from "./pages/Estatisticas/index";
import Relatorios from "./pages/Relatorios/index";
import PizzasPorSabor from "./pages/Relatorios/PizzasPorSabor/index";
import PizzasPorTamanho from "./pages/Relatorios/PizzasPorTamanho/index";
import PizzasBrinde from "./pages/Relatorios/PizzasBrinde/index";
import PizzasPromocoes from "./pages/Relatorios/PizzasPromocoes/index";
import PedidosPorBairro from "./pages/Relatorios/PedidosPorBairro/index";
import Bebidas from "./pages/Relatorios/Bebidas/index";
import RodizioPizzas from "./pages/Relatorios/RodizioPizzas/index";
import SalaoPizzas from "./pages/Relatorios/SalaoPizzas/index";
import SalaoBebidas from "./pages/Relatorios/SalaoBebidas/index";
import Historicos from "./pages/Historicos/index";
import HistoricoPuloBorda from "./pages/HistoricoPuloBorda/index";
import LimpezaPendentes from "./pages/LimpezaPendentes/index";
import MetaColetiva from "./pages/Meta/Coletiva/index";
import MetaIndividual from "./pages/Meta/Individual/index";
import MeuPerfil from "./pages/MeuPerfil/index";
import Pizzaiolo from "./pages/Pizzaiolo/index";
import PizzaioloIntegracao from "./pages/PizzaioloIntegracao/index";
import Forneiro from "./pages/Forneiro/index";
import ProducaoPorcoes from "./pages/ProducaoPorcoes/index";
import ProducaoBebidas from "./pages/ProducaoBebidas/index";
import Bordeiro from "./pages/Bordeiro/index";
import PainelBordeiro from "./pages/PainelBordeiro/index";
import RankingPainelBordeiro from "./pages/Ranking/PainelBordeiro/index";
import Roterista from "./pages/Roterista/index";
import RoteristaAutomatico from "./pages/RoteristaAutomatico/index";
import RankingProducao from "./pages/Ranking/Producao/index";
import RankingMontagem from "./pages/Ranking/Montagem/index";
import RankingEntrega from "./pages/Ranking/Entrega/index";
import RankingMetaColetiva from "./pages/Ranking/MetaColetiva/index";
import RankingMetricasQualidade from "./pages/Ranking/MetricasQualidade/index";
import RankingFichaDiaria from "./pages/Ranking/FichaDiaria/index";
import RankingFichaSetores from "./pages/Ranking/FichaSetores/index";
import RetiradaPedidosCliente from "./pages/Ranking/Retiradas/index";
import WebhookCardapio from "./pages/Webhooks/Cardapio/index";
import Cardapio from "./pages/Cardapio/index";
import Bordas from "./pages/Bordas/index";
import Tamanhos from "./pages/Tamanhos/index";
import FilasIntegracao from "./pages/FilasIntegracao/index";
import FilaPorcoes from "./pages/FilaPorcoes/index";
import FilaBebidas from "./pages/FilaBebidas/index";
import RodizioSequencia from "./pages/Rodizio/index";
import RodizioUrgencia from "./pages/Rodizio/Urgencia";
import AguardandoEntrega from "./pages/AguardandoEntrega/index";
import Painel from "./pages/Painel/index";
import SequenciaPedidos from "./pages/SequenciaPedidos/index";
import Inspecao from "./pages/Inspecao/index";
import Whatsapp from "./pages/Whatsapp/index";
import ConfiguracaoAcessos from "./pages/ConfiguracaoAcessos/index";
import AtualizarApk from "./pages/AtualizarApk/index";
import MensagensMobile from "./pages/MensagensMobile/index";
import ControleProducaoItensPage from "./pages/ControleProducao/Itens/index";
import ControleProducaoSetoresPage from "./pages/ControleProducao/Setores/index";
import ControleProducaoBaseSemanalPage from "./pages/ControleProducao/BaseSemanal/index";
import ControleProducaoFuncionamentoPage from "./pages/ControleProducao/Funcionamento/index";
import ControleProducaoFichaDiariaPage from "./pages/ControleProducao/FichaDiaria/index";
import ControleProducaoFichaTransferenciaPage from "./pages/ControleProducao/FichaTransferencia/index";
import ControleProducaoSobrasPage from "./pages/ControleProducao/Sobras/index";
import FerramentasDesenvolvedor from "./pages/FerramentasDesenvolvedor/index";
import MonitoramentoServidor from "./pages/MonitoramentoServidor/index";
import BackupsServidor from "./pages/BackupsServidor/index";
import CronsServidor from "./pages/CronsServidor/index";
import ControleProducaoUndMedidasPage from "./pages/ControleProducao/UndMedidas/index";
import ControleProducaoRelatorioConsumoPage from "./pages/ControleProducao/RelatorioConsumo/index";
import ControleProducaoRelatorioTemposPage from "./pages/ControleProducao/RelatorioTempos/index";
import ControleProducaoRelatorioEquipePage from "./pages/ControleProducao/RelatorioEquipe/index";
import NotFound from "./pages/NotFound/index";

import { queryClient } from "./lib/queryClient";
import { BrazilDate } from "./utils/BrazilDate";

BrazilDate.setupGlobalDate();

function App() {
  return (
    <ErrorBoundaryWrapper>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lojas"
                element={
                  <ProtectedRoute>
                    <Loja />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usuarios"
                element={
                  <ProtectedRoute>
                    <Usuario />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/entregadores"
                element={
                  <ProtectedRoute>
                    <Entregador />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/estatisticas"
                element={
                  <ProtectedRoute>
                    <Estatisticas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/pizzas-por-sabor"
                element={
                  <ProtectedRoute>
                    <PizzasPorSabor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/pizzas-por-tamanho"
                element={
                  <ProtectedRoute>
                    <PizzasPorTamanho />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/pizzas-brinde"
                element={
                  <ProtectedRoute>
                    <PizzasBrinde />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/pizzas-promocoes"
                element={
                  <ProtectedRoute>
                    <PizzasPromocoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/pedidos-por-bairro"
                element={
                  <ProtectedRoute>
                    <PedidosPorBairro />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/bebidas"
                element={
                  <ProtectedRoute>
                    <Bebidas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/rodizio-pizzas"
                element={
                  <ProtectedRoute>
                    <RodizioPizzas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/salao-pizzas"
                element={
                  <ProtectedRoute>
                    <SalaoPizzas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios/salao-bebidas"
                element={
                  <ProtectedRoute>
                    <SalaoBebidas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios"
                element={
                  <ProtectedRoute>
                    <Relatorios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/historicos"
                element={
                  <ProtectedRoute>
                    <Historicos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/historico-pulo-borda"
                element={
                  <ProtectedRoute>
                    <HistoricoPuloBorda />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/limpeza-pendentes"
                element={
                  <ProtectedRoute>
                    <LimpezaPendentes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/metas/coletiva"
                element={
                  <ProtectedRoute>
                    <MetaColetiva />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/metas/individual"
                element={
                  <ProtectedRoute>
                    <MetaIndividual />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meu-perfil"
                element={
                  <ProtectedRoute>
                    <MeuPerfil />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pizzaiolo"
                element={
                  <ProtectedRoute>
                    <Pizzaiolo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pizzaiolo-integracao"
                element={
                  <ProtectedRoute>
                    <PizzaioloIntegracao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/forneiro"
                element={
                  <ProtectedRoute>
                    <Forneiro />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/producao-porcoes"
                element={
                  <ProtectedRoute>
                    <ProducaoPorcoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/producao-bebidas"
                element={
                  <ProtectedRoute>
                    <ProducaoBebidas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bordeiro"
                element={
                  <ProtectedRoute>
                    <Bordeiro />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/painel-bordeiro"
                element={
                  <ProtectedRoute>
                    <PainelBordeiro />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roterista"
                element={
                  <ProtectedRoute>
                    <Roterista />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roterista-automatico"
                element={
                  <ProtectedRoute>
                    <RoteristaAutomatico />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/producao"
                element={
                  <ProtectedRoute noLayout>
                    <RankingProducao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/montagem"
                element={
                  <ProtectedRoute noLayout>
                    <RankingMontagem />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/entregas"
                element={
                  <ProtectedRoute noLayout>
                    <RankingEntrega />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/metas-coletiva"
                element={
                  <ProtectedRoute noLayout>
                    <RankingMetaColetiva />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/painel-bordeiro"
                element={
                  <ProtectedRoute noLayout>
                    <RankingPainelBordeiro />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/metricas-qualidade"
                element={
                  <ProtectedRoute noLayout>
                    <RankingMetricasQualidade />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/retirada"
                element={
                  <ProtectedRoute noLayout>
                    <RetiradaPedidosCliente />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/ficha-diaria"
                element={
                  <ProtectedRoute noLayout>
                    <RankingFichaDiaria />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ranking/ficha-setores"
                element={
                  <ProtectedRoute noLayout>
                    <RankingFichaSetores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cardapio"
                element={
                  <ProtectedRoute>
                    <Cardapio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bordas"
                element={
                  <ProtectedRoute>
                    <Bordas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tamanhos"
                element={
                  <ProtectedRoute>
                    <Tamanhos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/filas-integracao"
                element={
                  <ProtectedRoute>
                    <FilasIntegracao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fila-porcoes"
                element={
                  <ProtectedRoute>
                    <FilaPorcoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fila-bebidas"
                element={
                  <ProtectedRoute>
                    <FilaBebidas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rodizio"
                element={
                  <ProtectedRoute>
                    <RodizioSequencia />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rodizio/urgencia"
                element={
                  <ProtectedRoute>
                    <RodizioUrgencia />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aguardando-entrega"
                element={
                  <ProtectedRoute>
                    <AguardandoEntrega />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/painel"
                element={
                  <ProtectedRoute>
                    <Painel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sequencia-pedidos"
                element={
                  <ProtectedRoute>
                    <SequenciaPedidos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inspecao"
                element={
                  <ProtectedRoute>
                    <Inspecao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp"
                element={
                  <ProtectedRoute>
                    <Whatsapp />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/webhooks/cardapio"
                element={
                  <ProtectedRoute>
                    <WebhookCardapio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/perfis/permissoes"
                element={
                  <ProtectedRoute>
                    <ConfiguracaoAcessos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuracao-acessos"
                element={<Navigate to="/perfis/permissoes" replace />}
              />
              <Route
                path="/atualizar-apk"
                element={
                  <ProtectedRoute>
                    <AtualizarApk />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mensagens-mobile"
                element={
                  <ProtectedRoute>
                    <MensagensMobile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao"
                element={
                  <Navigate to="/controle-producao/ficha-diaria" replace />
                }
              />
              <Route
                path="/controle-producao/itens"
                element={
                  <ProtectedRoute>
                    <ControleProducaoItensPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/setores"
                element={
                  <ProtectedRoute>
                    <ControleProducaoSetoresPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/base-semanal"
                element={
                  <ProtectedRoute>
                    <ControleProducaoBaseSemanalPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/funcionamento"
                element={
                  <ProtectedRoute>
                    <ControleProducaoFuncionamentoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/ficha-diaria"
                element={
                  <ProtectedRoute>
                    <ControleProducaoFichaDiariaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/ficha-transferencia"
                element={
                  <ProtectedRoute>
                    <ControleProducaoFichaTransferenciaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/sobras"
                element={
                  <ProtectedRoute>
                    <ControleProducaoSobrasPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/relatorio-consumo"
                element={
                  <ProtectedRoute>
                    <ControleProducaoRelatorioConsumoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/relatorio-tempos"
                element={
                  <ProtectedRoute>
                    <ControleProducaoRelatorioTemposPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/relatorio-equipe"
                element={
                  <ProtectedRoute>
                    <ControleProducaoRelatorioEquipePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/controle-producao/und-medida"
                element={
                  <ProtectedRoute>
                    <ControleProducaoUndMedidasPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ferramentas-desenvolvedor"
                element={
                  <ProtectedRoute>
                    <FerramentasDesenvolvedor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monitoramento"
                element={
                  <ProtectedRoute>
                    <MonitoramentoServidor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/backups"
                element={
                  <ProtectedRoute>
                    <BackupsServidor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crons"
                element={
                  <ProtectedRoute>
                    <CronsServidor />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<SmartRedirect />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <DeployVersionGuard />
            <ToastContainer position="top-right" autoClose={3000} />
          </Router>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundaryWrapper>
  );
}

export default App;
